/**
 * MarkdownEditor React component
 * 
 * @author    Jens Gruschel
 * @copyright © 2018 contexagon GmbH
 */


// react
import React from 'react';
import PropTypes from 'prop-types';

// material UI
import {withStyles} from "@material-ui/core";
import TextField from '@material-ui/core/TextField';
import ReactMarkdown from 'react-markdown';
import BottomNavigation from "@material-ui/core/BottomNavigation/BottomNavigation";
import BottomNavigationAction from "@material-ui/core/BottomNavigationAction/BottomNavigationAction";

// icons
import EditIcon from '@material-ui/icons/Edit';
import VerticalSplitIcon from '@material-ui/icons/VerticalSplit';
import SubjectIcon from '@material-ui/icons/Subject';
import HelpIcon from '@material-ui/icons/Help';



/**
 * MarkdownEditor is a React component for editing wiki text
 *
 * @author Jens Gruschel
 */
export class MarkdownEditor extends React.Component {

    state = {
        mode: null
    };

    handleModeChange = (event, value) => {
        this.setState({mode: value});
    }

    static getDerivedStateFromProps(props, state) {
        if (!state.mode) {
            return {mode: props.value ? "view" : "split"};
        }
        else if (!props.value) {
            if (state.mode !== "edit" && state.mode !== "split") {
                return {mode: "split"};
            }
        }
        return null;
    }

    render() {

        if (this.props.readOnly) {

            return (
                <ReactMarkdown className="markdown" source={this.props.value} />
            );
        }
        else {

            const { classes } = this.props;

            const inputProps = {
                style: {
                    fontFamily: "monospace,monospace"
                },
                readOnly: this.props.readOnly
            };

            const navigation = <BottomNavigation
                showLabels
                value={this.props.representation}
                onChange={this.handleModeChange}
                className={classes.navigationRoot}>
                <BottomNavigationAction label="Ansicht" value="view" icon={<SubjectIcon />} classes={{ root: classes.navigationButton, selected: classes.navigationButtonSelected }} />
                <BottomNavigationAction label="Geteilt" value="split" icon={<VerticalSplitIcon />} classes={{ root: classes.navigationButton, selected: classes.navigationButtonSelected }} />
                <BottomNavigationAction label="Bearbeiten" value="edit" icon={<EditIcon />} classes={{ root: classes.navigationButton, selected: classes.navigationButtonSelected }} />
                <BottomNavigationAction label="Hilfe" value="help" icon={<HelpIcon />} classes={{ root: classes.navigationButton, selected: classes.navigationButtonSelected }} />
            </BottomNavigation>;

            switch (this.state.mode) {

                case "split":

                    return (
                        <div>
                            {navigation}
                            <div className={this.props.classes.half}>
                                <TextField
                                    id={this.props.id}
                                    label={this.props.label}
                                    value={this.props.value}
                                    className={this.props.classes.codeField}
                                    InputProps={inputProps}
                                    onChange={this.props.onChange}
                                    margin="normal"
                                    multiline={true}
                                    fullWidth={true}
                                    type={"text"}
                                    variant={"outlined"}
                                    rows={15}
                                    rowsMax={50}
                                />
                            </div>
                            <div className={this.props.classes.half}>
                                <ReactMarkdown className="markdown" source={this.props.value} />
                            </div>
                        </div>
                    );
    
                case "edit":

                    return (
                        <div>
                            {navigation}
                            <TextField
                                id={this.props.id}
                                label={this.props.label}
                                value={this.props.value}
                                className={this.props.classes.codeField}
                                InputProps={inputProps}
                                onChange={this.props.onChange}
                                margin="normal"
                                multiline={true}
                                fullWidth={true}
                                type={"text"}
                                variant={"outlined"}
                                rows={15}
                                rowsMax={50}
                            />
                        </div>
                    );

                case "help":

                    const help = "Formatierung\n=====================\n\n" +
                        "Überschriften\n-------------\n\nÜberschriften werden mit einem oder mehreren Gleichheitszeichen `=` oder Minuszeichen `-` in der folgenden Zeile unterstrichen.\n\n\n    Kapitel\n    =======\n\n    Abschnitt\n    ---------\n\n> Kapitel\n> =======\n\n> Abschnitt\n> ---------\n\nAlternativ lassen sich eine Anzahl von Doppelkreuzen `#` unmittelbar vor (und wahlweise nach) der Überschrift nutzen.\n\n    # Kapitel #\n    ## Hauptabschnitt\n    ### Unterabschnitt\n\n> # Kapitel #\n> ## Hauptabschnitt\n> ### Unterabschnitt\n\n\n" +
                        "Umbrüche\n--------\n\nMindestens eine Leerzeile sorgt für einen neuen Absatz.\n\nZwei Leerzeichen am Zeilenende` ` ` `  \nbewirken einen Zeilenumbruch.\n\n\n" +
                        "Hervorhebung\n------------\n\n*Kursiv* und **fett** kann man Textstellen hervorheben mit ein oder zwei Sternen `*` oder Unterstrichen `_` vor und hinter den Wörtern.\n\n    *kursiv* oder _kursiv_  \n    **fett** oder __fett__  \n    _lässt sich auch **kombinieren**_\n\n> *kursiv* oder _kursiv_  \n> **fett** oder __fett__  \n> _lässt sich auch **kombinieren**_\n\nMit zwei Tildezeichen `~~` vor und nach der Textstelle lässt sich diese ~~streichen~~.\n\n    ~~gestrichen~~\n\n> ~~gestrichen~~\n\n\n" +
                        "Links\n-----\n\nLinks werden mittels URL automatisch erzeugt oder mit `[Text](URL)` explizit angegeben.\n\n    https://contexagon.com\n\n> https://contexagon.com\n\n    [Markdown auf GitHub](https://guides.github.com/features/mastering-markdown/)\n\n> [Markdown auf GitHub](https://guides.github.com/features/mastering-markdown/)\n\n\n" +
                        "Bilder\n------\n\nBilder mit alternativem Text bettet man unter Angabe der URL mit `![Text](URL)` ein.\n\n    ![Beispielbild](images/image.svg)\n\n> ![Beispielbild](images/image.svg)\n\n\n" +
                        "Listen\n------\n\nMit `-` oder `*` am Zeilenanfang beginnt man eine Liste.\n\n    * erstens\n    * zweitens\n    * drittens\n\n> * erstens\n> * zweitens\n> * drittens\n\nMit `1.` am Anfang jeder Zeile legt man eine nummerierte Aufzählung fest.\n\n    1. erstens\n    1. zweitens\n    1. drittens\n\n> 1. erstens\n> 1. zweitens\n> 1. drittens\n\nDas lässt sich auch verschachteln.\n\n    * erstens\n      1. dies\n      1. und jenes\n    * zweitens\n    * drittens\n\n> * erstens\n>   1. dies\n>   1. und jenes\n> * zweitens\n> * drittens\n\n\n" +
                        "Tabellen\n--------\n\nTabellen erzeugt man folgendermaßen mit den Zeichen `|` und `-`.\n\n    | Land        | Hauptstadt |\n    |-------------|------------|\n    | Deutschland | Berlin     |\n    | Frankreich  | Paris      | \n    | Österreich  | Wien       |\n\n> | Land        | Hauptstadt |\n> |-------------|------------|\n> | Deutschland | Berlin     |\n> | Frankreich  | Paris      | \n> | Österreich  | Wien       |\n\n\n" +
                        "Zitate\n------\n\nZitieren kann man mit dem Größerzeichen `>` am Zeilenanfang. Mit mehreren `>>` lassen sich Zitate verschachteln.\n\n    > Grau, teurer Freund, ist alle Theorie  \n    > Und grün des Lebens goldner Baum.\n\n    Johann Wolfgang von Goethe\n\n>> Grau, teurer Freund, ist alle Theorie  \n>> Und grün des Lebens goldner Baum.\n\n> Johann Wolfgang von Goethe\n\n\n" +
                        "Code\n----\n\nEin Stück `Code` kann man mit *Backticks* unterbringen.\n\n    alles markieren mit `Strg`+`A`\n\n> alles markieren mit `Strg`+`A`\n\nFür mehrzeilige Code-Blocks braucht es drei *Backticks* am Anfang und am Ende des Blocks oder eine Einrückung von mindestens vier Leerzeichen am Zeilenanfang.\n\n    ```\n    if x² + y² < 1 {\n      inside = true\n    }\n    ```\n\n> ```\nif x² + y² < 1 {\n  inside = true\n}\n```\n\n\n" +
                        "Linien\n------\n\nMindestens drei Minuszeichen `-` nach einer Leerzeile ergeben eine Linie.\n\n    ---\n\n> ---";

                    return (
                        <div className="markdownhelp">
                            {navigation}
                            <ReactMarkdown className="markdown" source={help} />
                        </div>
                    );

                default:

                    return (
                        <div>
                            {navigation}
                            <ReactMarkdown className="markdown" source={this.props.value} />
                        </div>
                    );
            }
        }
    }
}

MarkdownEditor.propTypes = {
    value: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    readOnly: PropTypes.bool,
};

const styles = {
    half: {
        display: "inline-block",
        verticalAlign: "top",
        width: "50%"
    },
    codeField: {
        marginTop: 16
    },
    navigationRoot: {
        width: "100%",
        background: "none",
        justifyContent: "left"
    },
    navigationButton: {
        paddingBottom: "0 !important",
        maxWidth: 100
    },
    navigationButtonSelected: {
        paddingBottom: 0
    }
};


export default withStyles(styles)(MarkdownEditor);